import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Flex, Spinner, Text } from "@chakra-ui/react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AppBar, APP } from "../../../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../../components/desktopUi";
import { formatVnd, } from "../../../utils/financeFormat";
import { fmtDate } from "../../../utils/format";
import { getBdcDisbursementListApi } from "../../../api/finance.bdc.api";

const TEXT = "#18212F";
const MUTED = "#94A3B8";
const BORDER = "#E5E9F0";
const WARNING = "#F79D0D";
const LIMIT = 10;

const Row = ({ label, value, color }) => (
  <Flex justify="space-between" gap="12px">
    <Text fontSize="13px" color={MUTED}>{label}</Text>
    <Text fontSize="13px" fontWeight={600} color={color || TEXT} textAlign="right">{value}</Text>
  </Flex>
);

const money = (v) => (v != null ? formatVnd(v) : "—");

const DisbursementCard = ({ item }) => {
  const isImport = item?.source === "IMPORT";
  return (
    <Box bg="white" border="1px solid" borderColor={BORDER} borderRadius="12px" p="14px">
      <Flex align="center" justify="space-between" gap="8px">
        <Text fontSize="14px" fontWeight={600} color={TEXT} noOfLines={1} flex="1">{item?.apartmentCode || "—"}</Text>
        <Box bg="#F1F4F9" borderRadius="6px" px="8px" py="2px">
          <Text fontSize="13px" fontWeight={500} color={isImport ? APP.primary200 : WARNING}>
            {isImport ? "Import" : "Giải ngân thủ công"}
          </Text>
        </Box>
      </Flex>
      <Box h="1px" bg={BORDER} my="12px" />
      <Flex direction="column" gap="8px">
        <Row label="Ngày giải ngân" value={fmtDate(item?.disbursementDate || item?.createdAt)} />
        <Row label="Tháng đối chiếu" value={item?.monthYear || "—"} />
        <Row label="Số trước thuế" value={money(item?.amountBeforeTax)} />
        <Row label="Thuế" value={money(item?.taxAmount)} />
        <Row label="Số tiền giải ngân" value={money(item?.disbursedAmount)} color={APP.primary200} />
      </Flex>
    </Box>
  );
};

const CcmgDisbursementDetail = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const { id } = useParams();
  const [search] = useSearchParams();
  const name = search.get("name") || "";
  const code = search.get("code") || "";

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);

  const fetchPage = useCallback(
    async (p, mode) => {
      if (!id) return;
      reqId.current += 1;
      const rid = reqId.current;
      setLoading(true);
      try {
        const data = await getBdcDisbursementListApi({ employeeId: id, page: p, limit: LIMIT });
        if (rid !== reqId.current) return;
        const list = data?.items || [];
        setItems((prev) => (mode === "append" ? [...prev, ...list] : list));
        setHasNext(!!data?.pagination?.hasNextPage);
        setPage(p);
      } catch {
        if (rid === reqId.current && mode !== "append") setItems([]);
      } finally {
        if (rid === reqId.current) setLoading(false);
      }
    },
    [id]
  );

  useEffect(() => {
    fetchPage(1, "replace");
  }, [fetchPage]);

  const pageBody = (
    <>
      <Box px="16px" pt="12px">
        {(name || code) && (
          <Text fontSize="14px" fontWeight={600} color={TEXT} mb="12px">
            {name}
            {code ? ` (${code})` : ""}
          </Text>
        )}
        {loading && !items.length ? (
          <Flex justify="center" py="32px"><Spinner color={APP.primary200} /></Flex>
        ) : items.length === 0 ? (
          <Text fontSize="12px" color="#808CA0" textAlign="center" py="32px">Chưa có dữ liệu lịch sử thanh toán CCMG</Text>
        ) : (
          <Flex direction="column" gap="10px">
            {items.map((it, i) => (
              <DisbursementCard key={it._id || i} item={it} />
            ))}
            {hasNext && (
              <Flex as="button" onClick={() => fetchPage(page + 1, "append")} justify="center" py="12px" color={APP.primary200} fontWeight={600} fontSize="14px">
                {loading ? <Spinner size="sm" /> : "Xem thêm"}
              </Flex>
            )}
          </Flex>
        )}
      </Box>
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage title="Lịch sử thanh toán CCMG" right={<DesktopBack onClick={() => navigate(-1)} />}>
        <DesktopPanel p="0" pb="24px">
          {pageBody}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="40px">
      <AppBar title="Lịch sử thanh toán CCMG" />
      {pageBody}
    </Box>
  );
};

export default CcmgDisbursementDetail;
